import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { X, Camera } from "@phosphor-icons/react";

// Safely stop a scanner instance. `stop()` throws SYNCHRONOUSLY when the
// scanner never reached the SCANNING or PAUSED state (e.g. permission denied,
// user closed the modal before the camera warmed up). We check state + wrap
// in try/catch AND guard the returned promise.
async function safeStop(scanner) {
  if (!scanner) return;
  try {
    const state = scanner.getState?.();
    const running = state === Html5QrcodeScannerState.SCANNING
                 || state === Html5QrcodeScannerState.PAUSED;
    if (running) {
      await scanner.stop();
    }
  } catch { /* swallow — nothing to stop */ }
  try { await scanner.clear?.(); } catch { /* ditto */ }
}

export default function BarcodeScanner({ onDetected, onClose }) {
  const divId = "kr-scanner-region";
  const scannerRef = useRef(null);
  const cbRef = useRef(onDetected);
  // Keep the latest callback in a ref so we don't need it in the effect dep
  // array (which would tear down + rebuild the scanner every parent render).
  useEffect(() => { cbRef.current = onDetected; }, [onDetected]);

  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(divId);
    scannerRef.current = scanner;
    (async () => {
      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 300, height: 120 } },
          (decoded) => {
            cbRef.current?.(decoded);
            safeStop(scanner);
          },
          () => {}
        );
        if (cancelled) safeStop(scanner);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      }
    })();
    return () => {
      cancelled = true;
      safeStop(scannerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg border border-zinc-300">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <Camera size={18} weight="bold" className="text-orange-600" />
            <div className="font-display font-black text-sm uppercase">Scan Barcode</div>
          </div>
          <button data-testid="scanner-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="p-4">
          <div id={divId} className="w-full border-4 border-dashed border-zinc-400" />
          {error && <p className="text-xs text-red-600 mt-3">{error}. Use manual entry instead.</p>}
          <p className="text-xs text-zinc-500 mt-3 font-mono">Align Code128 barcode within the box.</p>
        </div>
      </div>
    </div>
  );
}
